import { AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from 'src/app/core/services/data.service';
import { Utils } from 'src/app/core/services/utils';
import { ApplicationContext, RecentAgent, RecentApp, RecentLead, UserType } from 'src/app/models';
import { ReleaseNotes } from 'src/app/models/release-notes.model';
import { AlertModel } from 'src/app/models/alert.model';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { AdminRolePermissionService } from 'src/app/services/admin-role-permission.service';
import { HeaderMenuItem } from './models/header-menu.model';
import { HeaderMenuComponent } from './header-menu/header-menu.component';
import { ChannelService } from 'src/app/services/channel.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { CompanyService } from 'src/app/modules/global-admin/company/services/company.service';
import { FirebaseServicesService } from 'src/app/services/firebase';
import { firstValueFrom, Subscription, take } from 'rxjs';

@Component({
    selector: 'admin-header',
    templateUrl: './admin-header.component.html',
    styleUrls: ['./admin-header.component.scss'],
    standalone: false
})
export class AdminHeaderComponent extends ApplicationContextBoundComponent implements AfterViewInit {

  @ViewChild(HeaderMenuComponent)
  private headerMenu: HeaderMenuComponent;

  // Drawers are pinned 53px from the top of the viewport, where the topbar ends when nothing is above it.
  // Banners above the topbar (impersonation, release notes, voice profile) push it down, and because it paints
  // above drawers (.header-z-index) it would then cover the drawer's title bar and close button. The topbar's
  // current distance from the top of the viewport is published as --admin-topbar-offset so drawers start below it.
  @ViewChild('topbar', { static: true })
  private _topbar: ElementRef<HTMLElement>;

  @Input()
  user = {};

  @Input()
  brandLogoUrl: string = null;

  @Input()
  brandSmallLogoUrl: string = null;

  menu: HeaderMenuItem[] = [];

  config;

  @Input()
  unreadConversations: boolean;

  @Output()
  mentionsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  niceInContactClicked: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  notificationsClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  dialpadClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  alertsCreated: EventEmitter<AlertModel[]> = new EventEmitter<AlertModel[]>();

  @Output()
  conversationsToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  newLeadClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newAgentClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newContactClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newApplicationClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  viewLeadClicked: EventEmitter<RecentLead> = new EventEmitter<RecentLead>();

  @Output()
  viewAgentClicked: EventEmitter<RecentAgent> = new EventEmitter<RecentAgent>();

  @Output()
  testAudioSettingsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  releaseNotes: ReleaseNotes;
  companyId: number;
  userIsImpersonating: boolean;
  companyName: string;

  private _status: boolean = false;

  private _versionUpdateSubscription: Subscription;

  private _bannerMutationObserver: MutationObserver;
  private _bannerResizeObserver: ResizeObserver;
  private _topbarOffset: number = null;

  private baseMenu: HeaderMenuItem[] = [];
  private roleBlockedIdSet: Set<string> | null = null;

  constructor(
    private readonly injector: Injector,
    private readonly _router: Router,
    private readonly _dataService: DataService,
    private readonly adminRolePermissionService: AdminRolePermissionService,
    private readonly _firebaseServicesService: FirebaseServicesService,
    private readonly _navigationService: NavigationService,
    private readonly _channelService: ChannelService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _companyService: CompanyService,
    private readonly _ngZone: NgZone,
    private readonly _elementRef: ElementRef<HTMLElement>
  ) {
    super(injector);
    this._versionUpdateSubscription = this.applicationContextService.applicationVersionUpdated.subscribe(releaseNotes => {
      this.releaseNotes = releaseNotes;
    })
  }

  async ngOnInit() {
    const authData = this._localStorageService.authorizationData;
    this.companyId = this.applicationContext.userPermissions.companyId;
    this.userIsImpersonating = (this.applicationContext.userPermissions.superAdmin && this.applicationContext.currentlyLoggedInUserProfile.userProfile.companyId != 1) ||
      (authData && !!authData.impersonatingForLoanId);
    this.companyName = this.applicationContext.globalConfig.company.find(x => x.companyId == this.applicationContext.userPermissions.companyId)?.companyName;
    this._status = false;
    document.getElementById('navigation').style.left = '-280px';
    const url = this._router.url;

    try {
      await this.checkForReleaseNotesForUser(this.applicationContext);
    } catch (e) {
      // Don't block menu loading if release notes API fails
    }

    this._dataService.getRelativePath('./assets/app-data.json').subscribe((res) => {

      const url = this._router.url;
      const all: HeaderMenuItem[] = res.menu ?? [];
      this.config = res.app;

      if (this.companyId == 172 && all?.[0]?.children?.length > 3) {
        const moved = all[0].children.splice(3, 1);
        all[0].children.unshift(...moved);
      }

      this.baseMenu = this.filterByAdminPermission(all, this.applicationContext.userPermissions.admin);

      this.updateRoleVisibilityFromContext(this.applicationContext);

      this.recomputeMenu();

      this.removeExternalCompanyManagementMenuItemIfOnlyRetail();
      this.removeNewLeadIfLeadManagementIsNotEnabled();
      this.removeLeadsUnderMyListsIfLeadManagementIsNotEnabled();

      // Apply AdminRole permission filtering
      this.menu = this.filterMenuByAdminRolePermissions(this.menu);

      if (this.companyId == 289) {
        // update menu items for FBC here.
        const myLists = this.menu.find(x => x.text == 'My Lists');
        if (myLists?.children[0].text == 'Leads')
          myLists.children[0].text = 'Prospects';
        const myTools = this.menu.find(x => x.text == 'My Tools');
        if (myTools?.children[0].text == 'Dialer')
          myTools.children[0].text = 'Leads';
      }

      if (this.applicationContext && !this.applicationContext.userPermissions.superAdmin) {
        this.updateRecentlyVisitedList(this.applicationContext);
      }

      this.selectItem(url, this.menu);

    });
  }

  protected onContextChange(context: ApplicationContext): void {
    this.updateRoleVisibilityFromContext(context);
    this.recomputeMenu();
    this.menu = this.filterMenuByAdminRolePermissions(this.menu);
    this.updateRecentlyVisitedList(context);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    // Outside Angular: these fire on every scroll frame and only write a style, so they need no change detection.
    this._ngZone.runOutsideAngular(() => {
      // Scrolling the page moves the sticky topbar back up towards the top of the viewport
      window.addEventListener('scroll', this.publishTopbarOffset, { passive: true });
      // Banner text wraps onto more lines as the window narrows
      window.addEventListener('resize', this.publishTopbarOffset);
      // Banners are added and removed after load (release notes, voice profile, dismissals). This header renders
      // some of them and the admin layout (its parent) renders the rest, each as direct children. The page height
      // can't be watched instead: body has a min-height of 100vh, so on short pages it doesn't change.
      const host = this._elementRef.nativeElement;
      this._bannerMutationObserver = new MutationObserver(() => {
        this.observeBannerSizes();
        this.publishTopbarOffset();
      });
      this._bannerMutationObserver.observe(host, { childList: true });
      if (host.parentElement) {
        this._bannerMutationObserver.observe(host.parentElement, { childList: true });
      }
      // A banner that stays in place can still change height (web fonts swapping in, the company stylesheet
      // loading), which moves the topbar without adding or removing anything
      this._bannerResizeObserver = new ResizeObserver(this.publishTopbarOffset);
      this.observeBannerSizes();
    });
    this.publishTopbarOffset();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._versionUpdateSubscription) {
      this._versionUpdateSubscription.unsubscribe();
    }
    window.removeEventListener('scroll', this.publishTopbarOffset);
    window.removeEventListener('resize', this.publishTopbarOffset);
    this._bannerMutationObserver?.disconnect();
    this._bannerResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--admin-topbar-offset');
  }

  // Everything above the topbar: the banners this header renders before it, and the ones the layout renders
  // before this header. Re-collected whenever a banner is added or removed. The border box is watched so padding
  // and border changes count too, not only content height.
  private observeBannerSizes = () => {
    this._bannerResizeObserver.disconnect();
    for (let banner = this._topbar.nativeElement.previousElementSibling; banner; banner = banner.previousElementSibling) {
      this._bannerResizeObserver.observe(banner, { box: 'border-box' });
    }
    for (let banner = this._elementRef.nativeElement.previousElementSibling; banner; banner = banner.previousElementSibling) {
      this._bannerResizeObserver.observe(banner, { box: 'border-box' });
    }
  }

  // Set on the root element because the drawers are rendered by the layout and feature pages, not inside this header
  private publishTopbarOffset = () => {
    // Capped at a third of the window. Without the cap an unusually tall banner (e.g. voice profile listing many
    // campaigns) pushes the drawer's top below the bottom of the window and the drawer looks like it never opened.
    // Past the cap the topbar covers the top of the drawer, as it did before this offset existed.
    const offset = Math.min(Math.max(0, this._topbar.nativeElement.getBoundingClientRect().top), window.innerHeight / 3);
    if (offset === this._topbarOffset) {
      return;
    }
    this._topbarOffset = offset;
    document.documentElement.style.setProperty('--admin-topbar-offset', `${offset}px`);
  }

  onDismissVersionUpdateNoticeClicked = () => {
    const version = this.releaseNotes.version;
    this.releaseNotes = null;
    this._firebaseServicesService.viewReleaseNotes(version.toString()).subscribe({
      next: () => {
      },
      error: () => { }
    })
  }

  onAlertsCreated = (alerts: AlertModel[]) => {
    this.alertsCreated.emit(alerts);
  }

  onNotificationsClicked = (notifications: any) => {
    this.notificationsClicked.emit(notifications);
  }

  onMentionsClicked = (open: boolean) => {
    this.mentionsClicked.emit(open);
  }

  onNiceInContactClicked = (activeTab: string) => {
    this.niceInContactClicked.emit(activeTab);
  }

  onConversationsToggled = (open: boolean) => {
    this.conversationsToggled.emit(open);
  }

  onDialpadClicked = () => {
    this.dialpadClicked.emit();
  }

  toggleNav() {
    this._status = !this._status;
    if (this._status) {
      document.getElementById('navigation').style.left = '0px';
    } else {
      document.getElementById('navigation').style.left = '-280px';
      this.headerMenu?.collapseAllSubmenus();
    }
  }

  onMenuItemClicked = (menuItem: HeaderMenuItem) => {
    if (!menuItem.children?.length) {
      this.toggleNav();
    }
  }

  onNewLeadClicked = () => {
    this.newLeadClicked.emit();
  }

  onNewAgentClicked = () => {
    this.newAgentClicked.emit();
  }

  onNewContactClicked = () => {
    this.newContactClicked.emit();
  }

  onNewApplicationClicked = () => {
    this.newApplicationClicked.emit();
  }

  onViewLeadClicked = (lead: RecentLead) => {
    this.viewLeadClicked.emit(lead);
  }

  onViewAgentClicked = (agent: RecentAgent) => {
    this.viewAgentClicked.emit(agent);
  }

  onTestAudioSettingsClicked = (open: boolean) => {
    this.testAudioSettingsClicked.emit(open);
  }

  onStopImpersonatingClicked = () => {
    const authData = this._localStorageService.authorizationData;
    if (authData?.impersonatingAsAdmin || authData?.impersonatingForLoanId) {
      this.userIsImpersonating = false;
      this._router.navigate(['end-impersonation-mode'], {
        queryParams: {},
      });
    } else {
      const companyGuid = this.applicationContext.currentlyLoggedInUser.allUserCompanyData.find(x => x.companyId == 1)?.companyGuid;
      if (companyGuid) {
        const companyToGoTo = this.applicationContext.currentlyLoggedInUser.allUserCompanyData.find((c) => c.companyGuid === companyGuid && c.userType === UserType.Admin);
        if (companyToGoTo) {
          this._navigationService.navigateToCompanySwitchUrl(companyToGoTo, true);
        }
      }
    }
  }

  private async checkForReleaseNotesForUser(context: ApplicationContext) {
    const userReleaseNotesCurrentVersion = context?.currentlyLoggedInUserProfile?.userProfile?.releaseNotesVersion ?? '0.0.0';

    const companyReleaseNotes: ReleaseNotes | null =
      (await firstValueFrom(this._companyService.getCurrentReleaseNotes().pipe(take(1)))) ?? null;

    if (!companyReleaseNotes) return;

    if (companyReleaseNotes.version !== userReleaseNotesCurrentVersion) {
      this.applicationContextService.updateApplicationVersion(companyReleaseNotes);
    }
  }

  private removeExternalCompanyManagementMenuItemIfOnlyRetail = () => {
    if (this.applicationContext && this.applicationContext.userPermissions.enabledChannels !== "Disabled") {
      const enabledChannels = this._channelService.getChannelsFromCommaDelimitedString(this.applicationContext.userPermissions.enabledChannels);
      if (enabledChannels.length === 1 && enabledChannels[0].value.trim().toLowerCase() === 'retail') {
        const adminMenuItem = this.menu.find(mi => mi.text.trim().toLowerCase() === 'admin');
        if (adminMenuItem) {
          const externalCompanyManagementMenuItemIndex = adminMenuItem.children?.findIndex(mi => mi.text.trim().toLowerCase() === 'external company management');
          if (externalCompanyManagementMenuItemIndex >= 0) {
            adminMenuItem.children?.splice(externalCompanyManagementMenuItemIndex, 1);
          }
        }
      }
    }
  }

  private removeNewLeadIfLeadManagementIsNotEnabled = () => {
    if (!this.applicationContext.userPermissions.leadManagementEnabled) {
      const newMenuItem = this.menu.find(mi => mi.text.trim().toLowerCase() === 'new');
      if (newMenuItem) {
        const newLeadMenuItemIndex = newMenuItem.children.findIndex(mi => mi.text.trim().toLowerCase() === 'new lead');
        if (newLeadMenuItemIndex >= 0) {
          newMenuItem.children.splice(newLeadMenuItemIndex, 1);
        }
      }
    }
  }

  private removeLeadsUnderMyListsIfLeadManagementIsNotEnabled = () => {
    if (!this.applicationContext.userPermissions.leadManagementEnabled) {
      const myListsMenuItem = this.menu.find(mi => mi.text.trim().toLowerCase() === 'my lists');
      if (myListsMenuItem) {
        const leadsMenuItemIndex = myListsMenuItem.children.findIndex(mi => mi.text.trim().toLowerCase() === 'leads');
        if (leadsMenuItemIndex >= 0) {
          myListsMenuItem.children.splice(leadsMenuItemIndex, 1);
        }
      }
    }
  }

  private updateRecentlyVisitedList = (context: ApplicationContext) => {
    const recentMenuItems: HeaderMenuItem[] = [];
    if (context != null) {
      const { recentApplications, recentLeads, recentAgents } = context.userPermissions;

      if (recentApplications || recentLeads || recentAgents) {
        if (recentApplications?.length) {
          const recentAppChildren = recentApplications.map(app => new HeaderMenuItem(null, "fas fa-folder", app.displayText, `app-details/${app.applicationId}`));
          const recentAppItem = new HeaderMenuItem(recentAppChildren, 'fas fa-folder', 'Recent Apps', null);

          recentMenuItems.push(recentAppItem)
        }

        if (recentLeads?.length) {
          const recentLeadsChildren = recentLeads.map(lead => new HeaderMenuItem(null, "fas fa-flag", Utils.getPersonsDisplayName(lead) || "<Missing Borrower Name>", null, null, 'openLeadDrawer', { lead: lead }));
          const recentLeadItem = new HeaderMenuItem(recentLeadsChildren, 'fas fa-flag', 'Recent Leads', null);

          recentMenuItems.push(recentLeadItem)
        }

        if (recentAgents?.length) {
          const recentAgentsChildren = recentAgents.map(agent => new HeaderMenuItem(null, "fas fa-user-tie", Utils.getPersonsDisplayName(agent), null, null, 'openAgentDrawer', { agent: agent }));
          const recentAgentItem = new HeaderMenuItem(recentAgentsChildren, 'fas fa-user-tie', 'Recent Agents', null);

          recentMenuItems.push(recentAgentItem)
        }

        const recentlyVisited = new HeaderMenuItem(recentMenuItems, "fas fa-star", "Recent", null, null, null, null, 'RecentlyVisited');
        const existingOne = this.menu.find(mi => mi.id === 'RecentlyVisited' || mi.text === 'Recently Visited' || mi.text === 'Recent');
        if (!existingOne) {
          this.menu.push(recentlyVisited);
        } else {
          existingOne.children = recentlyVisited.children;
        }
      }

      if (!context.userPermissions.showNewSubmissionForm && !context.userPermissions.superAdmin) {
        const newMenuIndex = this.menu.findIndex(m => m.text == 'New');
        const newApplicationIndex = this.menu[newMenuIndex].children.findIndex(ch => ch.text == 'New Application');
        this.menu[newMenuIndex].children[newApplicationIndex].hide = true;
      }
    }
  }

  private selectItem = (url: string, elements: HeaderMenuItem[]) => {
    if (!elements) return;

    const item = elements.find(item => url.indexOf(item.link) > -1);
    if (item) {
      item.selected = true;
      return;
    }

    if (!item) {
      elements.forEach(el => {
        if (!el.children?.length) {
          return;
        }
        this.selectItem(url, el.children);
      })
    }
  }

  private filterMenuByAdminRolePermissions = (menuItems: HeaderMenuItem[]): HeaderMenuItem[] => {
    return menuItems.filter(item => {
      // Check AdminRole claim requirement
      if (item.requiredAdminClaim && !this.adminRolePermissionService.hasAdminRolePermission(item.requiredAdminClaim, this.applicationContext.userPermissions)) {
        return false;
      }

      // Recursively filter children
      if (item.children?.length) {
        item.children = this.filterMenuByAdminRolePermissions(item.children);
        // If all children are filtered out, hide the parent menu item
        if (item.children.length === 0) {
          return false;
        }
      }

      return true;
    });
  }


  private filterByAdminPermission(items: HeaderMenuItem[], hasAdmin: boolean): HeaderMenuItem[] {
    const walk = (it: HeaderMenuItem): HeaderMenuItem | null => {
      if (it?.requireAdminPermission && !hasAdmin) return null;

      const filteredChildren = (it.children ?? [])
        .map(walk)
        .filter((x): x is HeaderMenuItem => !!x);

      return { ...it, children: filteredChildren.length ? filteredChildren : undefined };
    };

    return (items ?? []).map(walk).filter((x): x is HeaderMenuItem => !!x);
  }

private updateRoleVisibilityFromContext(context: ApplicationContext): void {
  const list = context?.userPermissions?.disabledOpsMenuItems as string[] | undefined;

  if (!list || list.length === 0) {
    this.roleBlockedIdSet = null;
    return;
  }

  // 'Reporting' points straight at the reports hub and must always stay reachable,
  // so a role cannot hide it even when its id was persisted as disabled.
  this.roleBlockedIdSet = new Set(list.map(String).filter(id => id !== 'Reporting'));
}

private filterMenuByRole(items: HeaderMenuItem[], blockedIds: Set<string> | null): HeaderMenuItem[] {

  if (blockedIds === null || blockedIds.size === 0) return items ?? [];

  const walk = (it: HeaderMenuItem): HeaderMenuItem | null => {
    const idStr = it?.id != null ? String(it.id) : '';

    if (idStr && blockedIds.has(idStr)) return null;

    const filteredChildren = (it.children ?? [])
      .map(walk)
      .filter((x): x is HeaderMenuItem => !!x);

    if (filteredChildren.length === 0 && !it.link && !idStr) {
      return null;  // Remove empty containers with no id
    }
    if (filteredChildren.length === 0 && idStr && blockedIds.has(idStr)) {
      return null;  // Remove explicitly blocked leaf items
    }

    return { ...it, children: filteredChildren.length > 0 ? filteredChildren : undefined };
  };

  return (items ?? []).map(walk).filter((x): x is HeaderMenuItem => !!x);
}

  private recomputeMenu(): void {
    if (!this.baseMenu?.length) return;
    this.menu = this.filterMenuByRole(this.baseMenu, this.roleBlockedIdSet);

    this.ensureCompanyExtras(this.menu);

    this.selectItem(this._router.url, this.menu);
  }

  private ensureCompanyExtras(target: HeaderMenuItem[]) {
    if (this.applicationContext.isCompanyDeepHaven) {
      let resources = target.find(mi => mi.text === "Resources");
      if (!resources) {
        resources = { text: "Resources", icon: "", link: undefined, externalLink: undefined, children: [] };
        target.push(resources);
      }
      resources.children = resources.children ?? [];
      if (!resources.children.some(ch => ch.text === "Resource Center")) {
        resources.children.push({
          text: "Resource Center",
          link: "https://deephavenmortgage.com/thehavenwholesaleresources/",
          icon: "",
          externalLink: true
        });
      }

      if (!target.some(mi => mi.text === "Training Videos")) {
        target.push({
          text: "Training Videos",
          link: "https://www.youtube.com/playlist?list=PLUiFrS9Dr-b0b5hEU-i6yNpdkqDAqU132",
          icon: "",
          externalLink: true
        });
      }
    }

    if (this.applicationContext.isCompanyPRMG) {
      if (!target.some(mi => mi.text === "Resources")) {
        const resourcesMenu: HeaderMenuItem = { text: "Resources", icon: "", link: "", externalLink: true, children: [] };
        resourcesMenu.children!.push(
          { text: "Micro Training Videos", link: "https://eprmg.net/Training/Videos/LEOMicroLearningVideos.html", icon: "", externalLink: true },
          { text: "Training Materials & Webinar Link", link: "https://eprmg.net/Training/Videos/LEO.html", icon: "", externalLink: true },
          { text: "WS Turn Times", link: "https://eprmg.net/ResourceCenter/TurnTimes/WholesaleTurnTimes.pdf", icon: "", externalLink: true },
          { text: "WS Resource Center", link: "https://www.eprmg.net/ResourceCenter/WholesaleRC.pdf", icon: "", externalLink: true },
          { text: "WS Product Profiles", link: "https://www.eprmg.net/guidelines/PRMGProductProfileLinksWholesale.pdf", icon: "", externalLink: true },
          { text: "Request System Access", link: "https://www.prmg.net/wholesale-online-sign-up-request/", icon: "", externalLink: true },
          { text: "Subscribe to Rate Sheet", link: "https://www.prmg.net/rate-sheet-form/", icon: "", externalLink: true },
          { text: "VA Payment", link: "https://leo.prmg.net/tpo/va-payment", icon: "", externalLink: true },
          { text: "Corr Turn Times", link: "https://eprmg.net/ResourceCenter/TurnTimes/CorrTurnTimes.pdf", icon: "", externalLink: true },
          { text: "Correspondent Resource Center", link: "https://www.eprmg.net/ResourceCenter/CorrespondentRC.pdf", icon: "", externalLink: true },
          { text: "Correspondent Product Profiles", link: "https://www.eprmg.net/guidelines/PRMGProductProfileLinksCorrespondent.pdf", icon: "", externalLink: true },
          { text: "Delegated Contact and Tip Sheet", link: "https://www.eprmg.net/ResourceCenter/PoliciesProceduresInformation/DelegatedCorrespondentContactandTipSheet.pdf", icon: "", externalLink: true },
          { text: "NDC Contact and Tip Sheet", link: "https://www.eprmg.net/ResourceCenter/PoliciesProceduresInformation/Non-DelegatedCorrespondentContactandTipSheet.PDF", icon: "", externalLink: true },
        );
        target.push(resourcesMenu);
      }
    }
  }

}
